import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { firebaseService } from '../../services/firebaseService';
import { useNotification } from '../../hooks/useNotification';
import { Phone, Mail, Globe, Save, ArrowLeft, Facebook, Instagram, Linkedin, Twitter } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Empresa } from '../../services/firebaseService';

interface RedesSociais {
  facebook?: string;
  instagram?: string;
  linkedin?: string;
  twitter?: string;
  website?: string;
}

const Contatos: React.FC = () => {
  const { userProfile } = useAuth();
  const navigate = useNavigate();
  const { showSuccess, showError, NotificationComponent } = useNotification();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    telefone: '',
    site: '',
    redesSociais: {
      facebook: '',
      instagram: '',
      linkedin: '',
      twitter: '',
      website: ''
    } as RedesSociais
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    loadEmpresa();
  }, [userProfile?.empresaCnpj]);

  const loadEmpresa = async () => {
    if (!userProfile?.empresaCnpj) return;

    try {
      setLoading(true);
      const empresa = await firebaseService.getEmpresa(userProfile.empresaCnpj);
      
      if (empresa) {
        setFormData({
          email: empresa.email || '',
          telefone: empresa.telefone || '',
          site: empresa.site || '',
          redesSociais: {
            facebook: empresa.redesSociais?.facebook || '',
            instagram: empresa.redesSociais?.instagram || '',
            linkedin: empresa.redesSociais?.linkedin || '',
            twitter: empresa.redesSociais?.twitter || '',
            website: empresa.site || ''
          }
        });
      }
    } catch (error: any) {
      console.error('Erro ao carregar empresa:', error);
      showError('Erro ao carregar', 'Não foi possível carregar os dados da empresa.');
    } finally {
      setLoading(false);
    }
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Email inválido';
    }

    if (formData.site && !/^https?:\/\/.+/.test(formData.site)) {
      newErrors.site = 'URL deve começar com http:// ou https://';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleRedesSociaisChange = (rede: keyof RedesSociais, value: string) => {
    setFormData(prev => ({
      ...prev,
      redesSociais: {
        ...prev.redesSociais,
        [rede]: value
      }
    }));
  };

  const maskPhone = (value: string): string => {
    return value
      .replace(/\D/g, '')
      .replace(/^(\d{2})(\d)/, '($1) $2')
      .replace(/(\d{4,5})(\d{4})$/, '$1-$2')
      .substring(0, 15);
  };

  const handleSave = async () => {
    if (!userProfile?.empresaCnpj) return;
    if (!validate()) return;

    try {
      setSaving(true);
      await firebaseService.updateEmpresa(userProfile.empresaCnpj, {
        email: formData.email,
        telefone: formData.telefone,
        site: formData.site || formData.redesSociais.website,
        redesSociais: {
          facebook: formData.redesSociais.facebook || undefined,
          instagram: formData.redesSociais.instagram || undefined,
          linkedin: formData.redesSociais.linkedin || undefined,
          twitter: formData.redesSociais.twitter || undefined
        }
      });
      showSuccess('Contatos atualizados', 'Os dados de contato foram atualizados com sucesso!');
    } catch (error: any) {
      console.error('Erro ao salvar:', error);
      showError('Erro ao salvar', error.message || 'Não foi possível salvar os dados de contato.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <NotificationComponent />
      
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/configuracoes')}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="h-5 w-5 text-gray-600" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Contatos</h1>
            <p className="text-gray-600">Configure os canais de contato da sua empresa</p>
          </div>
        </div>
        <button
          onClick={handleSave}
          disabled={saving}
          className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Save className="h-4 w-4" />
          {saving ? 'Salvando...' : 'Salvar'}
        </button>
      </div>

      {/* Form */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 space-y-6">
        {/* Contatos Principais */}
        <div>
          <div className="flex items-center gap-2 mb-4">
            <Phone className="h-5 w-5 text-primary-600" />
            <h2 className="text-lg font-semibold text-gray-900">Contatos Principais</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  className={`w-full pl-10 pr-3 py-2 border rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                    errors.email ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="contato@empresa.com.br"
                />
              </div>
              {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Telefone
              </label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  value={formData.telefone}
                  onChange={(e) => handleInputChange('telefone', maskPhone(e.target.value))}
                  className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="(11) 99999-9999"
                />
              </div>
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Website
              </label>
              <div className="relative">
                <Globe className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  value={formData.site}
                  onChange={(e) => handleInputChange('site', e.target.value)}
                  className={`w-full pl-10 pr-3 py-2 border rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                    errors.site ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="https://www.empresa.com.br"
                />
              </div>
              {errors.site && <p className="text-red-500 text-xs mt-1">{errors.site}</p>}
            </div>
          </div>
        </div>

        {/* Redes Sociais */}
        <div>
          <div className="flex items-center gap-2 mb-4">
            <Globe className="h-5 w-5 text-primary-600" />
            <h2 className="text-lg font-semibold text-gray-900">Redes Sociais</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <div className="flex items-center gap-2">
                  <Facebook className="h-4 w-4 text-blue-600" />
                  Facebook
                </div>
              </label>
              <input
                type="text"
                value={formData.redesSociais.facebook || ''}
                onChange={(e) => handleRedesSociaisChange('facebook', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="https://facebook.com/empresa"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <div className="flex items-center gap-2">
                  <Instagram className="h-4 w-4 text-pink-600" />
                  Instagram
                </div>
              </label>
              <input
                type="text"
                value={formData.redesSociais.instagram || ''}
                onChange={(e) => handleRedesSociaisChange('instagram', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="https://instagram.com/empresa"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <div className="flex items-center gap-2">
                  <Linkedin className="h-4 w-4 text-blue-700" />
                  LinkedIn
                </div>
              </label>
              <input
                type="text"
                value={formData.redesSociais.linkedin || ''}
                onChange={(e) => handleRedesSociaisChange('linkedin', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="https://linkedin.com/company/empresa"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <div className="flex items-center gap-2">
                  <Twitter className="h-4 w-4 text-blue-400" />
                  Twitter
                </div>
              </label>
              <input
                type="text"
                value={formData.redesSociais.twitter || ''}
                onChange={(e) => handleRedesSociaisChange('twitter', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="https://twitter.com/empresa"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contatos;

